import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import EnquiryTable from '../../../components/tables/EnquiryTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useEnquiryApi } from '../../../hooks/useEnquiryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { EnquiryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const EnquiryList: React.FC = () => {
  const { search, remove, isLoading, error } = useEnquiryApi();
  const [items, setItems] = useState<EnquiryDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Enquiries', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Enquiries:', error);
      return 0;
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: EnquiryDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Enquiry:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/enquiries"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Manage.Enquiry.View'
    >
      <Card
        title="Manage Enquiries"
        subtitle={`Total Records: ${totalRecords}`}
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="name"
              label="Search by Name"
              value={searchFilters.name || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, name: e.target.value })}
              placeholder="Search by Name"
            />
            <FormField
              id="surname"
              label="Search by Surname"
              value={searchFilters.surname || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, surname: e.target.value })}
              placeholder="Search by Surname"
            />
            <FormField
              id="emailAddress"
              label="Search by Email Address"
              value={searchFilters.emailAddress || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, emailAddress: e.target.value })}
              placeholder="Search by Email Address"
            />
            <FormField
              id="contact"
              label="Search by Contact"
              value={searchFilters.contact || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, contact: e.target.value })}
              placeholder="Search by Contact"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <EnquiryTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/enquiries/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default EnquiryList;